import { ClockIcon, MailIcon, PhoneIcon } from 'lucide-react'
import React from 'react'

import { type ConciergeTeamContent } from './siteChromeContent'
import { cn } from './ui/cn'
import { FeatureList, FeatureListItem } from './ui/FeatureList'
import { Wrapper } from './ui/Wrapper'
import { AltBlock } from './ui_patterns/AltBlock'

const CopyBlock = Wrapper
const ColBlock = Wrapper

interface ConciergeTeamProps {
  content: ConciergeTeamContent
}

// Insert a soft break opportunity before the "@" so a long support email wraps
// there on narrow screens instead of overflowing the card. <wbr> only breaks
// when the line can't fit, so it stays on one line whenever there's room.
const emailWithBreakBeforeAt = (display: string): React.ReactNode => {
  const atIndex = display.indexOf('@')
  if (atIndex === -1) return display

  return (
    <>
      {display.slice(0, atIndex)}
      <wbr />
      {display.slice(atIndex)}
    </>
  )
}

export const ConciergeTeam = ({ content }: ConciergeTeamProps): React.ReactElement => {
  const { heading, body, supportPoints, cardTitle, phone, email, hours } = content
  return (
    <AltBlock>
      <div className="snplay:grid-cols-12 snplay:items-center snplay:gap-8 snplay:sm:grid">
        <CopyBlock className="snplay:col-span-5 snplay:max-sm:mb-12 snplay:md:col-span-6">
          <h1
            className={cn(
              'snplay:mb-8 snplay:text-center snplay:text-4xl',
              'snplay:font-bold snplay:sm:text-left',
            )}
          >
            {heading}
          </h1>
          <p
            className={cn(
              'snplay:text-justify snplay:text-base snplay:text-pretty',
              'snplay:text-ui-secondary snplay:md:text-left',
            )}
          >
            {body}
          </p>
          {supportPoints && supportPoints.length > 0 && (
            <FeatureList className="snplay:my-8">
              {supportPoints.map((point) => (
                <FeatureListItem key={point.title} title={point.title}>
                  <p className="snplay:text-base snplay:text-ui-secondary">{point.body}</p>
                </FeatureListItem>
              ))}
            </FeatureList>
          )}
        </CopyBlock>
        <ColBlock
          className={cn(
            'snplay:col-span-7',
            'snplay:md:col-span-6 snplay:lg:col-span-5 snplay:lg:col-start-8',
          )}
        >
          <div
            className={cn(
              'snplay:rounded-2xl snplay:bg-ui-primary',
              'snplay:p-8 snplay:text-ui-foreground',
            )}
          >
            <h2 className="snplay:mb-8 snplay:text-xl snplay:font-bold snplay:text-primary">
              {cardTitle}
            </h2>
            <div className="snplay:space-y-4 snplay:text-base">
              <a
                href={phone.href}
                className={cn(
                  'snplay-link snplay-can-focus snplay:flex',
                  'snplay:w-fit snplay:items-center snplay:gap-6',
                  'snplay:hover:text-primary',
                )}
              >
                <PhoneIcon
                  className={cn('snplay:my-1 snplay:size-5', 'snplay:shrink-0 snplay:text-primary')}
                />
                {phone.display}
              </a>
              <a
                href={email.href}
                className={cn(
                  'snplay-link snplay-can-focus snplay:flex',
                  'snplay:w-fit snplay:items-center snplay:gap-6',
                  'snplay:hover:text-primary',
                )}
              >
                <MailIcon
                  className={cn('snplay:my-1 snplay:size-5', 'snplay:shrink-0 snplay:text-primary')}
                />
                <span className="snplay:min-w-0">{emailWithBreakBeforeAt(email.display)}</span>
              </a>
              <div className="snplay:flex snplay:@container">
                <ClockIcon
                  className={cn(
                    'snplay:my-1 snplay:mr-6 snplay:size-5',
                    'snplay:shrink-0 snplay:text-primary',
                  )}
                />
                <ul>
                  {hours.map((entry) => (
                    <li
                      key={entry.label}
                      className="snplay:@max-xs:mb-2 snplay:gap-4 snplay:@xs:flex"
                    >
                      <div className="snplay:min-w-25">{entry.label}</div>
                      <div>{entry.value}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </ColBlock>
      </div>
    </AltBlock>
  )
}
